// Tipos para Demandas de Pacientes

export type DemandStatus = 'OPEN' | 'IN_PROGRESS' | 'WAITING_PATIENT' | 'RESOLVED' | 'CANCELLED';

export type DemandType = 'DOUBT' | 'SYMPTOM' | 'MEDICATION' | 'APPOINTMENT' | 'EXAM' | 'OTHER';

export type DemandUrgency = 'LOW' | 'MEDIUM' | 'HIGH';

// Alias for backwards compatibility with forms
export type DemandPriority = DemandUrgency;

export type DemandSource = 'WHATSAPP' | 'PHONE' | 'EMAIL' | 'PORTAL' | 'IN_PERSON';

export type InteractionType = 'NOTE' | 'STATUS_CHANGE' | 'ASSIGNMENT' | 'RESOLUTION';

export interface Demand {
  id: string;
  patientName: string;
  patientId: string;
  type: DemandType;
  typeLabel: string;
  title: string;
  urgency: DemandUrgency;
  urgencyLabel: string;
  urgencyColor: string;
  status: DemandStatus;
  statusLabel: string;
  assignedToName?: string;
  assignedToId?: string;
  careLineName?: string;
  createdAt: string;
  updatedAt?: string;
  waitingTimeMinutes?: number;
}

export interface DemandDetail {
  id: string;
  // Patient info
  patientId: string;
  patientName: string;
  patientCpf?: string;
  patientPhone?: string;
  patientEmail?: string;
  patientAge?: number;
  // Demand info
  type: DemandType;
  typeLabel: string;
  title: string;
  description: string;
  urgency: DemandUrgency;
  urgencyLabel: string;
  urgencyColor: string;
  status: DemandStatus;
  statusLabel: string;
  // Care line
  careLineId?: string;
  careLineName?: string;
  // Assignment
  assignedToId?: string;
  assignedToName?: string;
  // Resolution
  resolution?: string;
  resolvedAt?: string;
  resolvedById?: string;
  resolvedByName?: string;
  // Timestamps
  createdAt: string;
  updatedAt?: string;
  waitingTimeMinutes?: number;
  // Interactions
  interactions: DemandInteraction[];
}

export interface DemandInteraction {
  id: string;
  type: InteractionType;
  content: string;
  previousStatus?: string;
  newStatus?: string;
  userId: string;
  userName: string;
  createdAt: string;
}

export interface DemandStats {
  totalOpen: number;
  totalInProgress: number;
  totalWaitingPatient: number;
  totalResolvedToday: number;
  totalResolvedWeek: number;
  totalResolvedMonth: number;
  averageResolutionTimeMinutes?: number;
  byType: Record<string, number>;
  byUrgency: Record<string, number>;
  byStatus: Record<string, number>;
}

export interface DemandFilters {
  search?: string;
  statuses?: DemandStatus[];
  types?: DemandType[];
  urgencies?: DemandUrgency[];
  assignedToId?: string;
  careLineId?: string;
  patientId?: string;
  dateFrom?: string;
  dateTo?: string;
  unassignedOnly?: boolean;
}

// Request types
export interface AssignDemandRequest {
  assignedToId: string;
  note?: string;
}

export interface UpdateDemandStatusRequest {
  status: DemandStatus;
  note?: string;
}

export interface ResolveDemandRequest {
  resolution: string;
  notifyPatient?: boolean;
}

export interface AddDemandNoteRequest {
  content: string;
  internal?: boolean;
}

// Staff create demand request
export interface CreateDemandRequest {
  patientId: string;
  type: DemandType;
  title: string;
  description: string;
  urgency: DemandUrgency;
  careLineId?: string;
  assignedToId?: string;
  notes?: string;
}

// Portal types
export interface PortalCreateDemandRequest {
  type: string;
  title: string;
  description: string;
  urgency?: string;
}

export interface PortalDemandResponse {
  id: string;
  type: string;
  typeLabel: string;
  title: string;
  description: string;
  status: string;
  statusLabel: string;
  urgency: string;
  createdAt: string;
  resolvedAt?: string;
  resolution?: string;
  resolvedByName?: string;
}

// Labels para exibição
export const DemandStatusLabels: Record<DemandStatus, string> = {
  OPEN: 'Aberta',
  IN_PROGRESS: 'Em Andamento',
  WAITING_PATIENT: 'Aguardando Paciente',
  RESOLVED: 'Resolvida',
  CANCELLED: 'Cancelada',
};

export const DemandStatusColors: Record<DemandStatus, { bg: string; text: string; border: string }> = {
  OPEN: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  IN_PROGRESS: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  WAITING_PATIENT: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  RESOLVED: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' },
};

export const DemandTypeLabels: Record<DemandType, string> = {
  DOUBT: 'Dúvida',
  SYMPTOM: 'Sintoma',
  MEDICATION: 'Medicamento',
  APPOINTMENT: 'Consulta',
  EXAM: 'Exame',
  OTHER: 'Outro',
};

export const DemandTypeIcons: Record<DemandType, string> = {
  DOUBT: 'HelpCircle',
  SYMPTOM: 'Thermometer',
  MEDICATION: 'Pill',
  APPOINTMENT: 'Calendar',
  EXAM: 'FileText',
  OTHER: 'MoreHorizontal',
};

export const DemandUrgencyLabels: Record<DemandUrgency, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
};

export const DemandUrgencyColors: Record<DemandUrgency, { bg: string; text: string; border: string }> = {
  LOW: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  MEDIUM: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  HIGH: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
};

export const InteractionTypeLabels: Record<InteractionType, string> = {
  NOTE: 'Anotação',
  STATUS_CHANGE: 'Mudança de Status',
  ASSIGNMENT: 'Atribuição',
  RESOLUTION: 'Resolução',
};
